import { motion } from "framer-motion";
import { MessageSquare, Target, Globe, Briefcase, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";

const services = [
  {
    icon: MessageSquare,
    title: "Gestione Comunicazione",
    description: "Gestiamo completamente la comunicazione della tua azienda: social media, contenuti, campagne pubblicitarie e molto altro. Un team dedicato al tuo brand.",
    highlight: true,
    price: "249",
    bonus: "Sito web in omaggio",
    features: ["Social Media Management", "Content Marketing", "Campagne ADV", "Email Marketing", "SEO & Copywriting"],
  },
  {
    icon: Target,
    title: "Digital Strategy",
    description: "Analizziamo il mercato, i competitor e il tuo posizionamento per costruire un piano strategico concreto con obiettivi misurabili.",
    features: ["Analisi di Mercato", "Piano Strategico", "KPI & Obiettivi", "Report Mensili"],
  },
  {
    icon: Globe,
    title: "Digital Export",
    description: "Ti accompagniamo nell'apertura di nuovi mercati esteri attraverso marketplace B2B come Alibaba e canali digitali internazionali.",
    features: ["Alibaba & Marketplace", "Strategia Export", "Lead Generation Estera", "Social Selling LinkedIn"],
  },
  {
    icon: Briefcase,
    title: "Consulenza Marketing B2B",
    description: "Supporto strategico per aziende B2B che vogliono generare lead qualificati e supportare il reparto commerciale con il digitale.",
    features: ["Lead Generation B2B", "Funnel & Automazioni", "LinkedIn Strategy", "Formazione Team"],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5 }
  }),
};

export default function ServicesSection() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="servizi" data-testid="services-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-100 rounded-full px-4 py-1.5 mb-4">
            <Sparkles size={14} className="text-[#52B788]" />
            <span className="text-xs font-medium text-[#1B4332] uppercase tracking-wider">I Nostri Servizi</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
            Cosa facciamo per te
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Strategie chiare, azioni concrete e risultati misurabili. Non vendiamo fumo: costruiamo percorsi digitali su misura per il tuo business.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={cardVariants}
              className={`relative rounded-2xl p-8 border transition-all duration-300 group overflow-hidden ${
                service.highlight
                  ? "bg-[#0F172A] text-white border-slate-700 md:col-span-2 ring-4 ring-[#0F172A]/5"
                  : "bg-white border-slate-100 hover:border-green-200 hover:shadow-lg"
              }`}
              data-testid={`service-card-${i}`}
            >
              {service.highlight && (
                <div className="absolute top-0 right-0 bg-[#52B788] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                  PIU' RICHIESTO
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                    service.highlight ? "bg-white/10" : "bg-green-50"
                  }`}>
                    <service.icon size={24} className={service.highlight ? "text-[#52B788]" : "text-[#1B4332]"} />
                  </div>

                  <h3 className={`text-2xl md:text-3xl font-medium mb-3 ${
                    service.highlight ? "text-white" : "text-slate-900"
                  }`} style={{ fontFamily: 'Outfit' }}>
                    {service.title}
                  </h3>

                  <p className={`leading-relaxed mb-6 ${
                    service.highlight ? "text-slate-300" : "text-slate-600"
                  }`}>
                    {service.description}
                  </p>

                  {service.highlight && (
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-sm text-slate-400">a partire da</span>
                      <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                        {service.price}
                      </span>
                      <span className="text-slate-400">/mese</span>
                      <span className="ml-3 inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium px-3 py-1 rounded-full">
                        <CheckCircle2 size={12} /> {service.bonus}
                      </span>
                    </div>
                  )}

                  {service.highlight && (
                    <button
                      onClick={() => scrollTo("#contatti")}
                      className="inline-flex items-center gap-2 bg-[#1B4332] hover:bg-[#143728] text-white rounded-full h-11 px-6 font-medium transition-all text-sm"
                      data-testid="service-cta-button"
                    >
                      Inizia Ora <ArrowRight size={16} />
                    </button>
                  )}
                </div>

                <div className={`flex-shrink-0 ${service.highlight ? "md:w-64" : ""}`}>
                  <ul className="space-y-3">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5">
                        <CheckCircle2 size={16} className={service.highlight ? "text-[#52B788]" : "text-emerald-500"} />
                        <span className={`text-sm ${
                          service.highlight ? "text-slate-300" : "text-slate-600"
                        }`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
